import React from 'react';
import { Link } from 'react-router-dom';

interface InquirySidebarProps {
    project: any;
    scrollToSection: (id: string) => void;
    setShowBrochureModal: (show: boolean) => void;
    handleInquirySubmit: (e: React.FormEvent) => void;
    inquiryForm: { 
        name: string; 
        email: string; 
        phone: string; 
        agreeContact: boolean; 
        agreeTerms: boolean;
        interestedHomeLoan: boolean;
        purpose: string;
        propertyType: string;
        propertySubType: string;
    };
    setInquiryForm: React.Dispatch<React.SetStateAction<{ 
        name: string; 
        email: string; 
        phone: string; 
        agreeContact: boolean; 
        agreeTerms: boolean;
        interestedHomeLoan: boolean;
        purpose: string;
        propertyType: string;
        propertySubType: string;
    }>>;
    inquirySubmitting: boolean;
    inquirySuccess: string;
    inquiryError: string;
    hideSiteVisit?: boolean;
}

const propertySubTypes: Record<string, string[]> = {
    'Residential': ['Apartment', 'Independent House/Villa', 'Floor', 'Bungalow', 'Penthouse', 'Duplex'],
    'Commercial': ['Office Space', 'Shop/Showroom', 'Commercial Land', 'Warehouse/Godown', 'Industrial Shed', 'Hotel/Guest House'],
    'PG/Co-living': ['Girls PG', 'Boys PG', 'Co-living Space', 'Guest House'],
    'Plot': ['Residential Plot', 'Commercial Plot', 'Industrial Plot', 'Agricultural Land'],
    'Land': ['Agricultural Land', 'Industrial Land', 'Institutional Land'],
    'Bank Auction Property': ['Residential', 'Commercial', 'Industrial', 'Agricultural']
};

const InquirySidebar: React.FC<InquirySidebarProps> = ({
    project,
    scrollToSection,
    setShowBrochureModal,
    handleInquirySubmit,
    inquiryForm,
    setInquiryForm,
    inquirySubmitting,
    inquirySuccess,
    inquiryError,
    hideSiteVisit = false
}) => {
    return (
        <div className="card border-0 shadow-[0_20px_50px_rgba(76,29,149,0.12)] rounded-3xl overflow-hidden bg-white ring-1 ring-gray-100 transition-all hover:shadow-[0_30px_60px_rgba(76,29,149,0.18)] relative" style={{ borderLeft: '8px solid #f1ae24' }}>

            {(() => {
                const pkg = project?.developer?.assignedPackage;
                let showCustomWa = false;
                let showCustomCall = false;

                if (pkg && pkg.categories) {
                    pkg.categories.forEach((cat: any) => {
                        cat.features.forEach((feat: string) => {
                            const f = feat.toLowerCase();
                            const isNegative = /\bno\b/.test(f) || f.includes('❌');
                            
                            if (f.includes('whatsapp') && !isNegative) {
                                showCustomWa = true;
                            }
                            
                            if ((f.includes('call') || f.includes('phone')) && !isNegative) {
                                showCustomCall = true;
                            }
                            
                            // Legacy fallback
                            if (f.includes('inquiry leads enabled') && !isNegative) {
                                showCustomWa = true;
                                showCustomCall = true;
                            }
                        });
                    });

                    // Maintain exact previous logic fallback just in case
                    let hasEmailLead = false;
                    let hasWhatsappLead = false;
                    pkg.categories.forEach((cat: any) => {
                        cat.features.forEach((feat: string) => {
                            const f = feat.toLowerCase();
                            if (f.includes('email lead alerts') && (f.includes('yes') || !/\bno\b/.test(f))) hasEmailLead = true;
                            if (f.includes('whatsapp leads') && (f.includes('yes') || !/\bno\b/.test(f))) hasWhatsappLead = true;
                        });
                    });
                    if (hasEmailLead && hasWhatsappLead) {
                        showCustomWa = true;
                        showCustomCall = true;
                    }
                }

                const callPhone = (showCustomCall && project?.contactPhone) ? project.contactPhone : '+919537002666';
                const waNumber = (showCustomWa && project?.whatsappNumber) ? project.whatsappNumber : '+919537002666';
                const cleanWaNumber = waNumber.replace(/\D/g, ''); // Extract only digits for the link

                return (
                    <>
            {/* Authority Mascot: Full Width Banner */}
            <div className="w-full overflow-hidden">
                <img 
                    src="/assets/img/monkey.png" 
                    alt="My Property Guru Mascot" 
                    className="w-full h-auto object-contain animate-color-tone"
                />
            </div>

            <div className="p-6 md:p-8 pt-0 md:pt-0">
                <style>{`
                    @keyframes color-tone {
                        0% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
                        50% { filter: brightness(1.1) saturate(1.2) hue-rotate(5deg); }
                        100% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
                    }
                    .animate-color-tone {
                        animation: color-tone 8s ease-in-out infinite;
                    }
                    @keyframes bounce-subtle {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-5px); }
                    }
                    .animate-bounce-subtle {
                        animation: bounce-subtle 3s ease-in-out infinite;
                    }
                `}</style>

                {/* Header: The Authority Pedestal */}


                {/* Investment Matrix: Fast Lane Actions */}
                <div className={`grid ${hideSiteVisit ? 'grid-cols-2' : 'grid-cols-3'} gap-2 mb-1`}>
                    <a href={`tel:${callPhone}`} className="group/box no-underline">
                        <div className="bg-white p-2 rounded-2xl flex flex-col items-center gap-1.5 transition-all hover:bg-slate-50 hover:shadow-lg hover:-translate-y-1 border-2 border-[#05144b]">
                            <div className="w-8 h-8 rounded-xl bg-[#05144b]/10 flex items-center justify-center transition-transform group-hover/box:rotate-12 text-lg">
                                📞
                            </div>
                            <span className="text-[#05144b] text-[11px] font-bold leading-none whitespace-nowrap">Call Now</span>
                        </div>
                    </a>
                    <a href={`https://wa.me/${cleanWaNumber}?text=Hi, I'm interested in ${project.name}`} target="_blank" rel="noopener noreferrer" className="group/box no-underline">
                        <div className="bg-white p-2 rounded-2xl flex flex-col items-center gap-1.5 transition-all hover:bg-slate-50 hover:shadow-lg hover:-translate-y-1 border-2 border-[#10B981]">
                            <div className="w-8 h-8 rounded-xl bg-[#10B981]/10 flex items-center justify-center transition-transform group-hover/box:rotate-12 text-lg">
                                💬
                            </div>
                            <span className="text-[#10B981] text-[11px] font-bold leading-none whitespace-nowrap">WhatsApp</span>
                        </div>
                    </a>
                    {!hideSiteVisit && (
                        <button onClick={() => scrollToSection('schedule-tour')} className="group/box bg-white p-2 rounded-2xl flex flex-col items-center gap-1.5 transition-all hover:bg-slate-50 hover:shadow-lg hover:-translate-y-1 border-2 border-[#f1ae24]">
                            <div className="w-8 h-8 rounded-xl bg-[#f1ae24]/10 flex items-center justify-center transition-transform group-hover/box:rotate-12 text-lg">
                                📅
                            </div>
                            <span className="text-[#f1ae24] text-[11px] font-bold leading-none whitespace-nowrap">Site Visit</span>
                        </button>
                    )}
                </div>

                {/* The Inquiry Protocol */}
                <div className="space-y-0">
                    <div className="text-center mb-0">
                        <h4 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] mb-0">Inquire Now</h4>

                    </div>

                    <form onSubmit={handleInquirySubmit} className="space-y-0 mt-0">


                        {/* Identity Protocol: Name & Email */}
                        <div className="space-y-0">
                            <div className="relative group/field">
                                <label className="text-xs font-bold text-[#05144b] mb-0 block ml-1">Your Name</label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px]">
                                        👤
                                    </div>
                                    <input
                                        type="text"
                                        className="w-full bg-slate-50 border-0 border-b-2 border-transparent pl-11 pr-4 py-2 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                        placeholder="Enter Full Name"
                                        required
                                        value={inquiryForm.name}
                                        onChange={e => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                            </div>


                        </div>

                        {/* Quantum Field: Mobile */}
                        <div className="relative group/field">
                            <label className="text-xs font-bold text-[#05144b] mb-0 block ml-1">Your Mobile Number</label>
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px]">
                                    📱
                                </div>
                                <input
                                    type="tel"
                                    className="w-full bg-slate-50 border-0 border-b-2 border-transparent pl-11 pr-4 py-2 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                    placeholder="Mobile Number"
                                    required
                                    value={inquiryForm.phone}
                                    onChange={e => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                                />
                            </div>
                        </div>



                        {/* Terms and Conditions Opt-in */}
                        <div className="flex items-start mt-0 gap-3 group/optin cursor-pointer">
                            <div 
                                className={`mt-0 w-5 h-5 rounded-lg border-2 flex shrink-0 items-center justify-center transition-all ${inquiryForm.agreeTerms ? 'bg-emerald-500 border-emerald-500' : 'border-emerald-500/30 group-hover/optin:border-emerald-500'}`}
                                onClick={() => setInquiryForm(prev => ({ ...prev, agreeTerms: !prev.agreeTerms }))}
                            >
                                {inquiryForm.agreeTerms && <span className="text-[10px]">✅</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-[#05144b] leading-tight mt-0">
                                    I agree to the <Link to="/terms" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Terms & Conditions</Link>
                                </p>
                                <p className="text-[10px] md:text-[11px] font-medium text-[#05144b]/70 leading-tight">
                                    and <Link to="/privacy-policy" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>.
                                </p>
                            </div>
                        </div>

                        {inquiryError && (
                            <div className="bg-rose-50 border border-rose-100 p-3 rounded-2xl flex items-center gap-3">
                                <i className="fa-solid fa-triangle-exclamation text-rose-500"></i>
                                <span className="text-rose-600 text-[10px] font-black uppercase tracking-tight">{inquiryError}</span>
                            </div>
                        )}
                        {inquirySuccess && (
                            <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-2xl flex items-center gap-3">
                                <i className="fa-solid fa-circle-check text-emerald-500"></i>
                                <span className="text-emerald-600 text-[10px] font-black uppercase tracking-tight">{inquirySuccess}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={inquirySubmitting}
                            className="w-full relative py-4 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-2xl overflow-hidden group shadow-xl active:scale-[0.98] transition-all hover:-translate-y-1 animate-pulse-button"
                        >
                            {/* Continuous Shimmer Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer-continuous pointer-events-none"></div>
                            
                            <div className="relative z-10 flex items-center justify-center gap-3">
                                <span className="text-sm font-black text-[#f1ae24] uppercase tracking-wider">
                                    {inquirySubmitting ? 'Submitting...' : 'Submit Inquiry'}
                                </span>
                                {!inquirySubmitting && <i className="fa-solid fa-paper-plane text-[#f1ae24] text-xs animate-plane-fly"></i>}
                            </div>
                        </button>

                        <style>{`
                            @keyframes plane-fly {
                                0% { transform: translate(0, 0) rotate(0); opacity: 1; }
                                20% { transform: translate(2px, -2px) rotate(-5deg); }
                                50% { transform: translate(15px, -15px) rotate(-10deg); opacity: 0; }
                                51% { transform: translate(-15px, 15px) rotate(-10deg); opacity: 0; }
                                100% { transform: translate(0, 0) rotate(0); opacity: 1; }
                            }
                            .animate-plane-fly {
                                animation: plane-fly 3s infinite ease-in-out;
                            }
                            @keyframes shimmer-continuous {
                                0% { transform: translateX(-150%); }
                                100% { transform: translateX(150%); }
                            }
                            .animate-shimmer-continuous {
                                animation: shimmer-continuous 3s infinite linear;
                            }
                            @keyframes pulse-button {
                                0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(5, 20, 75, 0.3); }
                                50% { transform: scale(1.01); box-shadow: 0 20px 25px -5px rgba(5, 20, 75, 0.4); }
                            }
                            .animate-pulse-button {
                                animation: pulse-button 3s ease-in-out infinite;
                            }
                        `}</style>


                        <p className="text-center text-[11px] font-medium text-[#05144b]/70">Secure & Encrypted Encryption • Privacy Guarded</p>
                    </form>
                </div>
            </div>
            </>
            );
            })()}
        </div>
    );
};

export default InquirySidebar;
